import { z } from 'zod';

/**
 * Where an invite stands — **derived, never stored**.
 *
 * There is no `status` column behind this. An invite carries three facts —
 * `acceptedAt`, `revokedAt`, `expiresAt` — and its state is what those three
 * plus the clock say. P23a1 established the reason in billing: *a clock beats a
 * column*. An `expired` status written into a row is a lie from the moment the
 * deadline passes until a sweep gets round to it, and every reader in between
 * has to know not to trust it.
 *
 * - `pending` — unspent, in date. The only state a code can be redeemed from.
 * - `accepted` — it produced a link. Terminal, and the link's own status is
 *   where that relationship's end is recorded (never here).
 * - `revoked` — the ward cancelled it before anybody used it.
 * - `expired` — nobody used it in time.
 */
export const GUARDIAN_INVITE_STATES = ['pending', 'accepted', 'revoked', 'expired'] as const;

export const GuardianInviteStateSchema = z.enum(GUARDIAN_INVITE_STATES);

export type GuardianInviteState = (typeof GUARDIAN_INVITE_STATES)[number];

/** The three stored facts a state is resolved from. */
export interface GuardianInviteTiming {
  acceptedAt: string | null;
  revokedAt: string | null;
  expiresAt: string;
}

/**
 * The invite's state at `nowIso`.
 *
 * **The branch order is part of the contract.** `accepted` is tested first
 * because it is the one fact that outlives the code: an invite redeemed an hour
 * ago on a deadline that has since passed produced a guardian who is reading
 * conversations right now, and reporting it as `expired` would tell the ward
 * nobody is linked. `revoked` then beats `expired` because somebody *acted* —
 * "you cancelled this" and "nobody used it in time" are different sentences and
 * the ward is owed the true one.
 */
export function resolveGuardianInviteState(
  timing: GuardianInviteTiming,
  nowIso: string,
): GuardianInviteState {
  if (timing.acceptedAt !== null) return 'accepted';
  if (timing.revokedAt !== null) return 'revoked';

  // Inclusive: the stored deadline is the first instant the code no longer
  // works, and the accept claim's own `expires_at > now()` predicate must agree
  // with this function exactly, or a code refused by the database would be
  // drawn as `pending` in the list beside it.
  return timing.expiresAt <= nowIso ? 'expired' : 'pending';
}

/**
 * Is this invite past redeeming?
 *
 * The complement of the one state `accept` will take, spelled out so a surface
 * deciding whether to offer "cancel" and a service deciding whether to refuse a
 * code are reading the same fact rather than each testing for `'pending'`.
 */
export function isSpentGuardianInvite(state: GuardianInviteState): boolean {
  return state !== 'pending';
}
